import {
  isStageKey,
  type StageKey,
} from '../components/config/StrongerNudgeConfig';

const STORAGE_KEY = 'strongerNudgeId';
const COMPLETION_ROUTE_KEY = 'strongerNudgeCompletionRoute';
const BACK_ROUTE_KEY = 'strongerNudgeBackRoute';
const STAGE_STACK_KEY = 'strongerNudgeStageStack';
const JOURNEY_KEY = 'strongerNudgeJourney';

type StrongerNudgeJourney = 'withdrawal' | 'transfer';

function getItem(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key) || null;
  } catch {
    return null;
  }
}

function setItem(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch {}
}

export function getStrongerNudgeId(): string | null {
  return getItem(STORAGE_KEY);
}

export function setStrongerNudgeId(id: string | null): void {
  setItem(STORAGE_KEY, id);
}

export function clearStrongerNudgeId(): void {
  setStrongerNudgeId(null);
}

export function getCompletionRoute(): string | null {
  return getItem(COMPLETION_ROUTE_KEY);
}

export function setCompletionRoute(route: string): void {
  setItem(COMPLETION_ROUTE_KEY, route);
}

export function clearCompletionRoute(): void {
  setItem(COMPLETION_ROUTE_KEY, null);
}

export function getBackRoute(): string | null {
  return getItem(BACK_ROUTE_KEY);
}

export function setBackRoute(route: string): void {
  setItem(BACK_ROUTE_KEY, route);
}

export function clearBackRoute(): void {
  setItem(BACK_ROUTE_KEY, null);
}

export function getJourney(): StrongerNudgeJourney | null {
  const value = getItem(JOURNEY_KEY);
  if (value === 'withdrawal' || value === 'transfer') return value;
  return null;
}

export function setJourney(journey: StrongerNudgeJourney): void {
  setItem(JOURNEY_KEY, journey);
}

export function clearJourney(): void {
  setItem(JOURNEY_KEY, null);
}

function getStageStack(): StageKey[] {
  const raw = getItem(STAGE_STACK_KEY);
  if (!raw) return [];

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (stage): stage is StageKey =>
        typeof stage === 'string' && isStageKey(stage),
    );
  } catch {
    return [];
  }
}

function setStageStack(stack: StageKey[]): void {
  if (stack.length === 0) {
    setItem(STAGE_STACK_KEY, null);
    return;
  }

  setItem(STAGE_STACK_KEY, JSON.stringify(stack));
}

export function clearStrongerNudgeStageStack(): void {
  setStageStack([]);
}

export function pushStrongerNudgeStageToStack(stage: StageKey): void {
  setStageStack([...getStageStack(), stage]);
}

export function popStrongerNudgeStageFromStack(): StageKey | null {
  const stack = getStageStack();
  const previousStage = stack.pop() ?? null;
  setStageStack(stack);
  return previousStage;
}
